/**
 * Enhanced Type System for Advanced Connection Validation
 * Provides clear separation between SIGNAL and DATA types with comprehensive validation support
 */

// SIGNAL Types - für Control Flow und Event Handling
export enum SignalType {
  TRIGGER = 'SIGNAL_TRIGGER',    // Manual triggers (Button clicks, user actions)
  EVENT = 'SIGNAL_EVENT',        // State change notifications
  FLOW = 'SIGNAL_FLOW',          // Workflow execution control
  ERROR = 'SIGNAL_ERROR',        // Error and exception signals
  SIGNAL_ANY = 'SIGNAL_ANY'      // Universal signal type (accepts all SIGNAL types)
}

// DATA Types - für Data Processing und Information Flow
export enum DataType {
  TEXT = 'DATA_TEXT',            // String/text data
  NUMBER = 'DATA_NUMBER',        // Numeric values
  IMAGE = 'DATA_IMAGE',          // Image data/references
  OBJECT = 'DATA_OBJECT',        // Complex objects/dictionaries
  ARRAY = 'DATA_ARRAY',          // Array/list data
  BOOLEAN = 'DATA_BOOLEAN',      // True/false values
  FILE = 'DATA_FILE',            // File references/uploads
  URL = 'DATA_URL',              // Web URLs/links
  JSON = 'DATA_JSON',            // JSON structured data
  ERROR = 'DATA_ERROR',          // Error data and exception information
  FILE_PATH = 'DATA_FILE_PATH',  // File paths and directory references  
  DATA_ANY = 'DATA_ANY'          // Universal data type (accepts all DATA types)
}

// Union Type for all enhanced types
export type EnhancedNodeType = SignalType | DataType;

// Type Categories for validation logic
export enum TypeCategory {
  SIGNAL = 'SIGNAL',
  DATA = 'DATA'
}

// Validation Result Interface
export interface ValidationResult {
  isValid: boolean;
  category: 'valid' | 'invalid' | 'warning';
  message?: string;
  suggestedTypes?: EnhancedNodeType[];
  autoConversion?: {
    possible: boolean;
    targetType: EnhancedNodeType;
    accuracy: 'high' | 'medium' | 'low';
  };
}

// Socket Information Interface
export interface EnhancedSocketInfo {
  id: string;
  nodeId: string;
  type: EnhancedNodeType;
  label: string;
  direction: 'input' | 'output';
  required: boolean;
  description?: string;
  acceptedTypes?: EnhancedNodeType[];  // For multi-type inputs
}

// Connection Information Interface
export interface ConnectionInfo {
  id: string;
  sourceSocket: EnhancedSocketInfo;
  targetSocket: EnhancedSocketInfo;
  validationResult: ValidationResult;
  createdAt: Date;
}

// Type Utility Functions
export const TypeUtils = {
  /**
   * Determine if a type is a SIGNAL type
   */
  isSignalType(type: EnhancedNodeType): type is SignalType {
    return Object.values(SignalType).includes(type as SignalType);
  },

  /**
   * Determine if a type is a DATA type
   */
  isDataType(type: EnhancedNodeType): type is DataType {
    return Object.values(DataType).includes(type as DataType);
  },

  /**
   * Get the category of a type
   */
  getTypeCategory(type: EnhancedNodeType): TypeCategory {
    if (this.isSignalType(type)) return TypeCategory.SIGNAL;
    if (this.isDataType(type)) return TypeCategory.DATA;
    throw new Error(`Unknown type: ${type}`);
  },

  /**
   * Get display name for a type
   */
  getTypeDisplayName(type: EnhancedNodeType): string {
    const displayNames: Record<EnhancedNodeType, string> = {
      [SignalType.TRIGGER]: 'Trigger',
      [SignalType.EVENT]: 'Event',
      [SignalType.FLOW]: 'Flow',
      [SignalType.ERROR]: 'Error',
      [SignalType.SIGNAL_ANY]: 'Any Signal',
      [DataType.TEXT]: 'Text',
      [DataType.NUMBER]: 'Number',
      [DataType.IMAGE]: 'Image',
      [DataType.OBJECT]: 'Object',
      [DataType.ARRAY]: 'Array',
      [DataType.BOOLEAN]: 'Boolean',
      [DataType.FILE]: 'File',
      [DataType.URL]: 'URL',
      [DataType.JSON]: 'JSON',
      [DataType.ERROR]: 'Error Data',
      [DataType.FILE_PATH]: 'File Path',
      [DataType.DATA_ANY]: 'Any Data'
    };
    return displayNames[type] || type;
  },

  /**
   * Get abbreviation for a type (for compact display)
   */
  getTypeAbbreviation(type: EnhancedNodeType): string {
    const abbreviations: Record<EnhancedNodeType, string> = {
      [SignalType.TRIGGER]: 'TRG',
      [SignalType.EVENT]: 'EVT', 
      [SignalType.FLOW]: 'FLW',
      [SignalType.ERROR]: 'ERR',
      [SignalType.SIGNAL_ANY]: 'SIG',
      [DataType.TEXT]: 'TXT',
      [DataType.NUMBER]: 'NUM',
      [DataType.IMAGE]: 'IMG',
      [DataType.OBJECT]: 'OBJ',
      [DataType.ARRAY]: 'ARR',
      [DataType.BOOLEAN]: 'BOL',
      [DataType.FILE]: 'FILE',
      [DataType.URL]: 'URL',
      [DataType.JSON]: 'JSON',
      [DataType.ERROR]: 'ERR',
      [DataType.FILE_PATH]: 'PATH',
      [DataType.DATA_ANY]: 'ANY'
    };
    return abbreviations[type] || type.slice(0, 3).toUpperCase();
  },

  /**
   * Get color for a type (for visual representation)
   */
  getTypeColor(type: EnhancedNodeType): string {
    if (this.isSignalType(type)) {
      if (type === SignalType.ERROR) return '#dc2626'; // Red for ERROR signals
      return '#ff6b35'; // Orange for other SIGNAL types
    }
    if (this.isDataType(type)) {
      if (type === DataType.ERROR) return '#ef4444'; // Light red for ERROR data
      if (type === DataType.DATA_ANY) return '#10b981'; // Green for ANY types
      return '#4f46e5'; // Blue for DATA types
    }
    return '#6b7280'; // Gray fallback
  },

  /**
   * Check if two types are in the same category
   */
  sameCategory(type1: EnhancedNodeType, type2: EnhancedNodeType): boolean {
    return this.getTypeCategory(type1) === this.getTypeCategory(type2);
  }
};

// Legacy Type Mapping (for migration)
export const LEGACY_TYPE_MAPPING: Record<string, EnhancedNodeType> = {
  'TRIGGER': SignalType.TRIGGER,
  'ERROR': SignalType.ERROR,
  'TEXT': DataType.TEXT,
  'STRING': DataType.TEXT,
  'NUMBER': DataType.NUMBER,
  'INT': DataType.NUMBER,
  'FLOAT': DataType.NUMBER,
  'IMAGE': DataType.IMAGE,
  'OBJECT': DataType.OBJECT,
  'ARRAY': DataType.ARRAY,
  'BOOLEAN': DataType.BOOLEAN,
  'BOOL': DataType.BOOLEAN,
  'FILE': DataType.FILE,
  'FILE_PATH': DataType.FILE_PATH,
  'PATH': DataType.FILE_PATH,
  'URL': DataType.URL,
  'JSON': DataType.JSON,
  'ERROR_DATA': DataType.ERROR,
  'ANY': DataType.DATA_ANY
};

export default {
  SignalType,
  DataType,
  TypeCategory,
  TypeUtils,
  LEGACY_TYPE_MAPPING
};